import React from "react";
import { Link } from "react-router-dom";
import { Plus, ShieldCheck, Wifi, QrCode, Stethoscope, FlaskConical, Pill, Banknote, LineChart, Users, Hospital, Building2, ArrowRight, CheckCircle2 } from "lucide-react";

const Container = ({ children, className = "" }) => (
  <div className={`max-w-[1200px] mx-auto px-6 ${className}`}>{children}</div>
);

const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`py-20 ${className}`}>{children}</section>
);

export default function Landing() {
  return (
    <div className="bg-white text-slate-900" data-testid="landing-page">
      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur border-b border-slate-200">
        <Container className="h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="w-8 h-8 rounded-md overflow-hidden ring-1 ring-slate-200"><img src="/vms-logo.png" alt="VMS" className="w-full h-full object-cover" /></div>
            <span className="font-semibold tracking-tight text-slate-900">Vitality Medical Software</span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
            <a href="#features" className="hover:text-slate-900">Product</a>
            <a href="#solutions" className="hover:text-slate-900">Solutions</a>
            <a href="#pricing" className="hover:text-slate-900">Pricing</a>
            <a href="#security" className="hover:text-slate-900">Security</a>
            <a href="#faq" className="hover:text-slate-900">Resources</a>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="header-signin" className="text-sm px-3 py-1.5 text-slate-700 hover:text-slate-900">Sign in</Link>
            <Link to="/login" data-testid="header-trial" className="text-sm px-4 py-2 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white font-medium transition-colors">Start free trial</Link>
          </div>
        </Container>
      </header>

      {/* Hero */}
      <Section className="pt-16 pb-14">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="chip chip-blue mb-5">Built for Uganda · Ready for East Africa</div>
              <h1 className="text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.05]">
                Digitize Healthcare. <br/><span className="text-[#1976D2]">Improve Patient Care.</span>
              </h1>
              <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
                Vitality Medical Software connects patient records, clinical care, laboratories, pharmacies, billing, and hospital operations through one secure healthcare platform.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/login" data-testid="hero-trial" className="px-5 py-3 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white font-semibold transition-colors">Start free trial</Link>
                <a href="#pricing" data-testid="hero-demo" className="px-5 py-3 rounded-lg border border-slate-300 hover:bg-slate-50 font-medium text-slate-800 transition-colors">Book demo</a>
              </div>

              <div className="mt-10 grid grid-cols-3 gap-6 max-w-lg">
                {[
                  { k: "135", v: "Facilities live" },
                  { k: "412K+", v: "Patient records" },
                  { k: "99.98%", v: "Uptime" },
                ].map((s) => (
                  <div key={s.v}><div className="stat-number text-3xl text-slate-900">{s.k}</div><div className="text-xs text-slate-500 mt-1">{s.v}</div></div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
                <img alt="VMS clinical workstation" src="https://images.pexels.com/photos/4989174/pexels-photo-4989174.jpeg" className="w-full h-[420px] object-cover" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/95 backdrop-blur border border-slate-200 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-[#e6f4ea] flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-[#2E7D32]" /></div>
                    <div>
                      <div className="text-sm font-semibold">SM 2401-0001 · Nakato Sarah</div>
                      <div className="text-xs text-slate-500">Consultation completed · Lab verified · Bill settled</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Container>
      </Section>

      {/* Trust bar */}
      <div className="border-y border-slate-200 bg-slate-50">
        <Container className="py-6 flex flex-wrap items-center justify-between gap-6 text-sm text-slate-600">
          {[
            "Built for Uganda",
            "Low-bandwidth ready",
            "Secure role-based access",
            "Multi-facility support",
            "QR patient identity",
            "FHIR-ready",
          ].map((t) => <div key={t} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> {t}</div>)}
        </Container>
      </div>

      {/* Features */}
      <Section id="features">
        <Container>
          <div className="max-w-2xl">
            <div className="section-heading mb-3">One platform</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Everything a modern facility needs</h2>
            <p className="text-slate-600 mt-3">From reception to discharge, VMS replaces paper files with structured, permissioned digital operations.</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Users,       t: "Electronic Medical Records", d: "Longitudinal charts with signed SOAP notes, vitals trends, diagnoses and history." },
              { icon: QrCode,      t: "QR Health Cards",            d: "Every patient gets a permanent Patient ID and printable QR card in AX 1234-0001 format." },
              { icon: FlaskConical,t: "Laboratory Workflow",         d: "Orders, barcoded specimens, reference ranges, critical-value alerts and verification." },
              { icon: Pill,        t: "Pharmacy & Inventory",        d: "Prescription queue, batch tracking, expiry & low-stock alerts, dispensing labels." },
              { icon: Banknote,    t: "Billing & Payments",          d: "Invoices, mobile money and card payments, receipts, discounts & insurance." },
              { icon: LineChart,   t: "Finance & Analytics",         d: "Revenue, expenditure, payroll and department profitability — for controllers & CFOs." },
              { icon: Stethoscope, t: "Clinical Workflows",          d: "Triage, encounters, orders, imaging & maternity — with role-based safety checks." },
              { icon: ShieldCheck, t: "Security by Design",          d: "RBAC, 2FA, audit logs, encryption at rest and in transit. DPPA-informed architecture." },
              { icon: Wifi,        t: "Offline Ready",               d: "PWA-based low-bandwidth mode with secure sync when connectivity returns." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="border border-slate-200 rounded-xl p-6 hover:shadow-md transition-shadow bg-white">
                <div className="w-10 h-10 rounded-lg bg-[#e6f0fb] flex items-center justify-center text-[#1565C0] mb-4"><Icon className="w-5 h-5" /></div>
                <div className="font-semibold text-slate-900">{t}</div>
                <div className="text-sm text-slate-600 mt-1.5 leading-relaxed">{d}</div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Solutions */}
      <Section id="solutions" className="bg-slate-50">
        <Container>
          <div className="max-w-2xl">
            <div className="section-heading mb-3">Solutions</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Tailored for every facility type</h2>
          </div>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Hospital,   t: "Hospitals",       d: "Multi-branch, admissions, theatre & finance depth." },
              { icon: Building2,  t: "Clinics",         d: "Fast registration, EMR & billing for outpatient care." },
              { icon: FlaskConical,t: "Laboratories",   d: "Standalone lab with catalogue, verification & analyzer hooks." },
              { icon: Pill,       t: "Pharmacies",      d: "Prescription queue, inventory & controlled-drug controls." },
              { icon: QrCode,     t: "Imaging Centres", d: "Requests, scheduling and radiologist reporting." },
              { icon: Users,      t: "Medical Centres", d: "General practice with maternity & child health." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className="bg-white border border-slate-200 rounded-xl p-6">
                <Icon className="w-6 h-6 text-[#1976D2]" />
                <div className="mt-3 font-semibold">{t}</div>
                <div className="text-sm text-slate-600 mt-1">{d}</div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* How it works */}
      <Section>
        <Container>
          <div className="max-w-2xl">
            <div className="section-heading mb-3">How it works</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">From reception to discharge in one flow</h2>
          </div>
          <ol className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Register the patient in the 4-step wizard.",
              "Generate the VMS Patient ID (e.g. AX 1234-0001) & QR card.",
              "Complete triage and doctor consultation.",
              "Send lab, imaging or pharmacy requests.",
              "Process billing & simulated Mobile Money / Card payment.",
              "Records are securely available for future visits.",
            ].map((step, i) => (
              <li key={i} className="border border-slate-200 rounded-xl p-6 bg-white">
                <div className="text-[11px] tracking-widest text-slate-500 font-semibold">STEP {String(i+1).padStart(2,"0")}</div>
                <div className="mt-2 text-slate-800">{step}</div>
              </li>
            ))}
          </ol>
        </Container>
      </Section>

      {/* Pricing */}
      <Section id="pricing" className="bg-slate-50">
        <Container>
          <div className="max-w-2xl">
            <div className="section-heading mb-3">Pricing</div>
            <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Plans that scale with your facility. 1-month free trial.</h2>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { name: "Laboratory", tier: "For diagnostic labs", features: ["Test catalogue","Result verification","QR labels","Analyzer-ready"] },
              { name: "Clinic",     tier: "For clinics & small facilities", features: ["Full EMR","Pharmacy & billing","Patient portal","2 branches"], featured: true },
              { name: "Hospital",   tier: "For hospitals & networks", features: ["Everything in Clinic","Admissions & Theatre","Finance & Payroll","Unlimited branches"] },
            ].map((p) => (
              <div key={p.name} className={`rounded-2xl border p-8 bg-white ${p.featured ? "border-[#1976D2] ring-2 ring-[#1976D2]/20 shadow-md" : "border-slate-200"}`}>
                {p.featured && <div className="chip chip-blue mb-3">Most popular</div>}
                <div className="text-sm font-semibold text-slate-500">{p.tier}</div>
                <div className="text-xl font-bold mt-1">{p.name} Plan</div>
                <div className="mt-4">
                  <div className="text-3xl font-bold tracking-tight">Contact sales</div>
                  <div className="text-sm text-slate-500 mt-1">Custom quote for your facility</div>
                </div>
                <ul className="mt-6 space-y-2 text-sm text-slate-700">
                  {p.features.map((f) => <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> {f}</li>)}
                </ul>
                <Link to="/login" data-testid={`pricing-cta-${p.name.toLowerCase()}`} className={`mt-7 block text-center px-4 py-2.5 rounded-lg font-semibold transition-colors ${p.featured ? "bg-[#1976D2] hover:bg-[#1565C0] text-white" : "border border-slate-300 text-slate-800 hover:bg-slate-50"}`}>
                  Start free trial <ArrowRight className="w-4 h-4 inline ml-1" />
                </Link>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* Security */}
      <Section id="security">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="section-heading mb-3">Security</div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Enterprise-grade healthcare security</h2>
              <p className="mt-4 text-slate-600">Role-based access, two-factor authentication, AES-256 encryption at rest, TLS 1.3 in transit, tamper-resistant audit logs and organization-level data isolation.</p>
              <ul className="mt-6 space-y-2 text-sm text-slate-700">
                {["Role-Based Access Control (28 clinical roles)","Two-factor authentication","Encrypted backups & disaster recovery","Immutable audit trail","Patient consent controls","Organization-isolated multi-tenant architecture"].map((f) => (
                  <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[#2E7D32]" /> {f}</li>
                ))}
              </ul>
              <div className="text-xs text-slate-500 mt-4">Note: production deployment requires legal review, penetration testing, staff training and operational controls.</div>
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200">
              <img alt="Modern clinic interior" src="https://images.pexels.com/photos/7789616/pexels-photo-7789616.jpeg" className="w-full h-[420px] object-cover" />
            </div>
          </div>
        </Container>
      </Section>

      {/* FAQ */}
      <Section id="faq" className="bg-slate-50">
        <Container>
          <div className="section-heading mb-3">FAQ</div>
          <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Frequently asked questions</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { q: "How long is the free trial?", a: "One full month with no card required. All Phase 1 modules are unlocked." },
              { q: "Does VMS work offline?", a: "Yes. Approved read-only records and selected write workflows continue during outages, then sync securely." },
              { q: "Can I migrate paper files?", a: "Our team assists with digitization and provides templates for bulk imports." },
              { q: "Who owns the data?", a: "Each organization owns its data. Records are isolated and encrypted per tenant." },
              { q: "How does patient privacy work?", a: "Consent is captured at registration, and every record view is audited with the user and reason." },
              { q: "Can I add multiple branches?", a: "Yes — the Hospital plan includes unlimited branches with shared or per-branch pricing." },
            ].map((f) => (
              <div key={f.q} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="font-semibold text-slate-900">{f.q}</div>
                <div className="text-sm text-slate-600 mt-1.5">{f.a}</div>
              </div>
            ))}
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section className="bg-[#0a1628] text-white">
        <Container>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight">Ready to leave paper behind?</h2>
              <p className="text-slate-300 mt-3">Book a demo with a VMS specialist or start your free trial today.</p>
            </div>
            <div className="flex gap-3 lg:justify-end">
              <Link to="/login" data-testid="cta-trial" className="px-5 py-3 rounded-lg bg-[#1976D2] hover:bg-[#1565C0] text-white font-semibold transition-colors">Start free trial</Link>
              <Link to="/login" data-testid="cta-demo" className="px-5 py-3 rounded-lg border border-white/25 hover:bg-white/10 font-medium transition-colors">Book demo</Link>
            </div>
          </div>
        </Container>
      </Section>

      {/* Footer */}
      <footer className="border-t border-slate-200 py-10">
        <Container className="flex flex-wrap justify-between items-center gap-4 text-sm text-slate-500">
          <div className="flex items-center gap-2"><div className="w-6 h-6 rounded overflow-hidden ring-1 ring-white/10"><img src="/vms-logo.png" alt="VMS" className="w-full h-full object-cover" /></div> © 2026 Vitality Software Limited</div>
          <div className="flex gap-5">
            <a href="#security" className="hover:text-slate-900">Security</a>
            <a href="#faq" className="hover:text-slate-900">Privacy</a>
            <a href="#faq" className="hover:text-slate-900">Terms</a>
            <a href="#" className="hover:text-slate-900">Contact</a>
          </div>
        </Container>
      </footer>
    </div>
  );
}
